import { useCallback, useMemo } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { EXPERIMENT_CONSTITUENT_PSF_EXPERIMENT, ResolvedExperimentData } from '@change-corgi/experiments/configs';

import {
	type ConstituentPsfPetition,
	getConstituentCount,
	getLocalSignerLocation,
} from '#src/app/pages/psf/shared/utils';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

type ConstituentPsfExperiment = ResolvedExperimentData<typeof EXPERIMENT_CONSTITUENT_PSF_EXPERIMENT>;

type ConstituentStatusLike = Readonly<{
	isConstituent: boolean;
	method?: string | null;
}> | null;

type ExposureProperties = Readonly<{
	is_constituent: boolean;
	identification_method: string;
	has_decision_maker: boolean;
	saw_location_leaders: boolean;
	saw_constituent_threshold: boolean;
}>;

/**
 * Data-derived segmentation flags for the `$exposure` event (CHANGE-100773): the identification result,
 * whether the petition has a decision maker, whether the screens can name the place ("Nashville
 * leaders") instead of the generic "decision makers", and whether the constituent-count social proof is
 * available to show. Derived from the data, not from what rendered, so control carries the same flags.
 */
function resolveExposureProperties(
	status: ConstituentStatusLike,
	petition: ConstituentPsfPetition,
): ExposureProperties {
	return {
		is_constituent: Boolean(status?.isConstituent),
		identification_method: status?.method ?? 'unknown',
		has_decision_maker: Boolean(petition.decisionMakersConnection.nodes.at(0)?.displayName),
		saw_location_leaders: Boolean(getLocalSignerLocation(petition)),
		saw_constituent_threshold: getConstituentCount(petition) > 1,
	};
}

/**
 * Whether a signer takes part in the experiment: an identified constituent, on mobile, with a real
 * (non-fallback) assignment and the access FC on. `treatExperiment` bypasses the config's `enabledIf`,
 * so the FC is read again here; `!fallback` keeps treated == exposed, since the exposure hook refuses to
 * fire on a fallback resolution.
 */
function isConstituentPsfParticipant(
	experiment: ConstituentPsfExperiment,
	status: ConstituentStatusLike,
	isMobile: boolean,
): boolean {
	return Boolean(status?.isConstituent) && isMobile && experiment.config.enabled && !experiment.fallback;
}

const storageKey = (petitionId: string): string => `constituent-psf-enrolled-${petitionId}`;

// In-flight treats, so two triggers before the first one settles share one treat. Once settled, the
// sessionStorage flag (set before the treat starts) is what stops a second enrollment.
const pendingEnrollments = new Map<string, Promise<void>>();

function wasEnrolledInThisTab(petitionId: string): boolean {
	try {
		return window.sessionStorage.getItem(storageKey(petitionId)) === 'true';
	} catch {
		return false;
	}
}

function markEnrolledInThisTab(petitionId: string): void {
	try {
		window.sessionStorage.setItem(storageKey(petitionId), 'true');
	} catch {
		// Storage unavailable (private mode, blocked): the in-memory check still covers this page.
	}
}

/**
 * Constituents-in-PSF (CHANGE-100768): returns `enroll(status)`, which treats the experiment and fires
 * `$exposure` once per petition per tab, the first time it is called for a participant.
 *
 * Two surfaces call it, so every arm is enrolled at the earliest point a variant could differ:
 * - the combo page's "Share petition" sheet, on open (where `v2` swaps in the new share screen);
 * - the share page, on render (where `v2` and `v4` show the new goal screen), for signers who reach it
 *   without having opened the sheet in this tab.
 *
 * The "already enrolled" flag lives in sessionStorage because the two surfaces are different page loads, and the
 * constituent status it depends on is itself tab-scoped.
 */
export function useEnrollConstituentPsfExperiment({
	petitionId,
	petition,
	experiment,
}: Readonly<{
	petitionId: string;
	petition: ConstituentPsfPetition;
	experiment: ConstituentPsfExperiment;
}>): (status: ConstituentStatusLike) => Promise<void> {
	const utilityContext = useUtilityContext();
	const isMobile = useIsMobile();

	// `treatExperiment` registers treatment server-side, but the resolved object stays treated:false, and
	// `useTrackExperimentExposure` no-ops on !treated, so the exposure object is armed with treated:true.
	// Safe only because the call below gates on treatExperiment's own return (server-confirmed
	// registration). Do not call trackExposure from anywhere else.
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	return useCallback(
		async (status: ConstituentStatusLike): Promise<void> => {
			if (!isConstituentPsfParticipant(experiment, status, isMobile)) return;

			const pending = pendingEnrollments.get(petitionId);
			if (pending) {
				await pending;
				return;
			}
			if (wasEnrolledInThisTab(petitionId)) return;

			markEnrolledInThisTab(petitionId);
			const enrollment = (async () => {
				// `treatExperiment` resolves `false` rather than throwing when the treatment did not land
				// server-side (network failure, bot session), so $exposure is gated on that return: firing
				// it for an unregistered treat would expose a session experiment_variations has no record of.
				if (await treatExperiment(utilityContext, experiment.experimentName)) {
					await trackExposure(resolveExposureProperties(status, petition)).catch(() => undefined);
				}
			})();
			pendingEnrollments.set(petitionId, enrollment);
			try {
				await enrollment;
			} finally {
				pendingEnrollments.delete(petitionId);
			}
		},
		[experiment, isMobile, petitionId, utilityContext, trackExposure, petition],
	);
}
